import { FormikErrors, useFormik } from 'formik';
import { GoogleAdsRequestTypeGq } from '../../../../../../../../graphql/projects/models/GoogleAdsRequestTypeGq';
import { ProjectGq } from '../../../../../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../../../../../graphql/projects/projectsApiService';
import { KeywordSearchLimits } from '../../../../../../../../logic/otherUtilities/constants/seedInputLimits';
import { CreateSearchFormState, validLongTailLanguages } from './SearchModels';
import { useProjectDefaults } from '../../../../../../../../logic/otherUtilities/useProjectDefaults';
import { GoogleAdsRequestTagInputGq } from '../../../../../../../../graphql/projects/mutations/googleAds/useCreateGoogleAdsRequestGq';

export function setupSearchFormForDataManagement(
  project: ProjectGq,
  reload: () => Promise<unknown>,
  onSubmit: () => unknown,
  initialType: GoogleAdsRequestTypeGq
) {
  // Mutations
  const createAdsRequestMutation =
    projectsApiService.mutations.googleAds.useCreateGoogleAdsRequest();

  // Initial search form values (default)
  const defaults = useProjectDefaults(project);

  let initialSearchValues: CreateSearchFormState = {
    requestName: '',
    requestType: initialType,
    country: defaults.volumeCountry,
    city: defaults.volumeCity,
    language: defaults.volumeLanguage,
    includeZeroVolume: project.defaultMinSearchVolume == 0,
    includeMonths: project.defaultIncludeMonths,
    includeRankData: false,
    source: '',
    suggestionsWord: false,
    suggestionsAlphabeticals: false,
    suggestionsNumbers: false,
    suggestionsComparisons: false,
    suggestionsPrepositions: false,
    suggestionsQuestions: false,
    tagKeywords: undefined,
    maxCharacters: 20000 * 20,
  };

  const formBag = useFormik<CreateSearchFormState>({
    // enableReinitialize: true, // In case a new request is clicked, we'll swap out the values
    initialValues: initialSearchValues,
    enableReinitialize: true,
    validate: (values) => {
      const errors: FormikErrors<CreateSearchFormState> = {};
      const seedCount = values.source?.trim()?.split(/[\n,]+/g)?.length;
      // Seed limits ------------
      if (
        values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL &&
        seedCount > KeywordSearchLimits.LONG_TAIL
      ) {
        errors.source = `More than ${KeywordSearchLimits.LONG_TAIL} autocomplete idea seeds in one search is not allowed.`;
      }
      if (
        values.requestType === GoogleAdsRequestTypeGq.RELATED &&
        seedCount > KeywordSearchLimits.RELATED
      ) {
        errors.source = `More than ${KeywordSearchLimits.RELATED} keyword idea seeds in one search is not allowed`;
      }
      // Remove error message since new UI is limited to one domain idea seed
      /* if (
        values.requestType === GoogleAdsRequestTypeGq.SITE &&
        seedCount > KeywordSearchLimits.SITE
      ) {
        errors.source = `More than ${KeywordSearchLimits.SITE} domain idea seeds in one search is not allowed.`;
      } */
      if (
        values.requestType === GoogleAdsRequestTypeGq.URL &&
        seedCount > KeywordSearchLimits.URL
      ) {
        errors.source = `More than ${KeywordSearchLimits.URL} url idea seeds in one search is not allowed.`;
      }
      if (
        values.requestType === GoogleAdsRequestTypeGq.EXACT &&
        seedCount > KeywordSearchLimits.EXACT
      ) {
        errors.source = `More than ${KeywordSearchLimits.EXACT} exact keyword seeds in one search is not allowed.`;
      }
      //----------------

      if (seedCount === 0 || !(values.source.length > 0) || values.source === '') {
        errors.source = 'This field is required and cannot be empty';
      }
      if (values.source.length >= values.maxCharacters) {
        errors.maxCharacters = `More than ${values.maxCharacters} characters is not allowed`;
      }

      const validLongtailLanguage =
        !values.language?.name || validLongTailLanguages.includes(values.language?.name);

      const anyLongTailSuggestions = !!(
        values.suggestionsWord ||
        values.suggestionsAlphabeticals ||
        values.suggestionsNumbers ||
        (values.suggestionsComparisons && validLongtailLanguage) ||
        (values.suggestionsQuestions && validLongtailLanguage) ||
        (values.suggestionsPrepositions && validLongtailLanguage)
      );

      if (values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL && !anyLongTailSuggestions) {
        errors.suggestionsWord = 'At least one option has to be selected';
      }

      return errors;
    },
    onSubmit: (values, actions) => {
      // Default name
      let name = values.requestName;
      if (!name.length) {
        const seeds = values.source.split(/[\n,]+/).filter((x) => !!x?.length);
        name = seeds.slice(0, 1).join(', ') + (seeds.length > 1 ? '...' : '');
      }

      // Clean keyword seeds
      const seeds = values.source
        ?.split(/[\n,]+/)
        .filter((v) => !!v?.length)
        .map((v) => v?.trim());

      // Longtail
      const validLongtailLanguage =
        !values.language?.name || validLongTailLanguages.includes(values.language?.name);

      // Handle tag keywords in case output ids are string
      const tagKeywordsFix: GoogleAdsRequestTagInputGq = {
        tagGroupName: values.tagKeywords?.tagGroupName,
        tagGroupId:
          typeof values.tagKeywords?.tagGroupId !== 'string'
            ? values.tagKeywords?.tagGroupId
            : null,
        tagName: values.tagKeywords?.tagName,
        tagId: typeof values.tagKeywords?.tagId !== 'string' ? values.tagKeywords?.tagId : null,
        useSeedKeywords: values.tagKeywords?.useSeedKeywords,
      };

      // Create keyword search
      return createAdsRequestMutation({
        projectId: project.id,
        name: name,
        type: values.requestType,
        seeds: seeds,
        locationId: !!values.city?.id ? values.city?.id : values.country?.id,
        locationName: !!values.city?.id ? values.city?.name : values.country?.name,
        languageId: values.language?.id,
        languageName: values.language?.name,
        minSearchVolume: values.includeZeroVolume ? 0 : 1,
        includeRankData: values.includeRankData,
        suggestionsWord: values.suggestionsWord,
        suggestionsAlphabeticals: values.suggestionsAlphabeticals,
        suggestionsNumbers: values.suggestionsNumbers,
        suggestionsComparisons: validLongtailLanguage && values.suggestionsComparisons,
        suggestionsQuestions: validLongtailLanguage && values.suggestionsQuestions,
        suggestionsPrepositions: validLongtailLanguage && values.suggestionsPrepositions,
        includeTagKeywords: values.includeTagKeywords,
        tagKeywords: values.includeTagKeywords ? tagKeywordsFix : null,
      })
        .then(reload)
        .then(() => actions.resetForm({ values: { ...values, source: '' } }))
        .then(onSubmit);
    },
  });

  return formBag;
}
